<script setup>
import * as Sentry from "@sentry/vue";
import { computed, ref, toRefs, toValue, watch } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import AccountCreationForm from "@/components/auth/AccountCreation/AccountCreationForm.vue";

const props = defineProps({
  application: {
    type: Object,
    required: true,
  },
  goToNextStep: {
    type: Function,
    required: true,
  },
});

const { application } = toRefs(props);
const { profile, upsertApplicationOrderAndSubmission, applicationId } = application.value;

const router = useRouter();
const store = useStore();

const session = computed(() => application.value?.session?.value);

const oktaId = computed(() => {
  return store.state.currentUser?.participantId;
});
const unsavedProgramData = computed(() => application.value?.unsavedProgramData?.value);

const logo = ref(import.meta.env.VITE_CDN_ENDPOINT + "/images/api-connect-logo.png");
const orderFailed = ref(false);

const isFacultyLed = computed(() => {
  return session.value?.channel?.name?.toLowerCase() === "custom";
});

const showPersonalizedAccountCreationMessage = computed(() => !isFacultyLed.value);
watch(
  () => profile.value,
  // trigger the application creation, order etc after retrieve profile data from service.
  // Note: this only happens when profile is undefined and new value is not, to prevent multiple triggers.
  async (newValue, oldValue) => {
    if (!oldValue && !!newValue) {
      try {
        await upsertApplicationOrderAndSubmission();
        await router.replace({
          name: "modify-application",
          params: {
            applicationId: toValue(applicationId),
            stage: "apply",
          },
        });
      } catch (error) {
        orderFailed.value = true;
        Sentry.captureException(error, {
          tags: {
            troubleshooting_note: "Account creation step in configurator error",
            error: error.message,
          },
        });
      }
    }
  },
);
const afterCreationAction = async () => {
  // Set profile data in vuex
  if (oktaId.value) {
    await store.dispatch("getProfile", oktaId.value);
  }
};
</script>
<template>
  <div
    class="font-montserrat text-indigo-base text-left bg-white w-full p-8 pt-4 pb-4 md:px-0 lg:px-0 mb-24"
    :class="{
      'max-w-4xl': $route.path !== '/create-account',
      'max-w-2xl': $route.path === '/create-account',
    }"
  >
    <div v-if="$route.path === '/create-account'" class="flex justify-center py-10">
      <img :src="logo" class="h-8 object-contain logo" alt="API" />
    </div>
    <p v-else-if="showPersonalizedAccountCreationMessage" class="pb-4">
      You personalized your experience! Now it's time to create an account, apply, and your information will be saved.
    </p>
    <p>
      Already have an account?
      <a :class="`${secondaryColorClassLinks} font-semibold hover:underline`" href="/sign-in">
        Log in here.
      </a>
    </p>

    <AccountCreationForm
      :order-failed="orderFailed"
      :unsaved-program-data="unsavedProgramData"
      :skip-updating-application-in-local-database="true"
      @after-creation-action="afterCreationAction"
    />
  </div>
</template>
